import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useTrialContext } from '../context/TrialContext';
import { useSubscription } from '../context/SubscriptionContext';
import { EXAM_PRESETS, PRESET_QUESTION_COUNT } from '../constants/presets';
import UpgradeModal from '../components/UpgradeModal';
import { 
  trackQuizGenerationStarted, 
  trackQuizGeneratedSuccess, 
  trackQuizGenerationFailed,
  trackStudyMaterialSelected,
  trackStudyMaterialUploaded,
  trackExamPresetSelected,
  trackAIQuestionSolverUsed,
  trackPDFExported,
} from '../services/analytics';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Free tier constants
const FREE_TIER_TEXT_LIMIT = 5000;
const FREE_TIER_MAX_QUESTIONS = 7;

const QuizGenerate = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { isDarkMode } = useTheme();
  const { 
    trialEmail, 
    trialStatus, 
    isTrialActive, 
    isSubscribed,
    recordQuizGeneration, 
    canGenerateQuiz,
    quizzesToday,
    quizzesLimit,
    daysRemaining,
    checkTrialStatus
  } = useTrialContext();
  
  const {
    subscription,
    isPremium,
    isFree,
    getDailyQuizzesRemaining,
    getMaxQuestions,
    getTextCharLimit,
    refreshSubscription
  } = useSubscription();

  // Main mode: 'generate' or 'solve'
  const [mainMode, setMainMode] = useState('generate');
  
  // Quiz generation state
  const [inputMode, setInputMode] = useState('text');
  const [text, setText] = useState('');
  const [url, setUrl] = useState('');
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imageName, setImageName] = useState('');
  const [audioFile, setAudioFile] = useState(null);
  const [audioName, setAudioName] = useState('');
  const [difficulty, setDifficulty] = useState('medium');
  const [numQuestions, setNumQuestions] = useState(8);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [lockedFeature, setLockedFeature] = useState(null);
  
  // Question solver state
  const [solveInputMode, setSolveInputMode] = useState('text');
  const [solveQuestion, setSolveQuestion] = useState('');
  const [solveFile, setSolveFile] = useState(null);
  const [solveFileName, setSolveFileName] = useState('');
  const [solvePdfFile, setSolvePdfFile] = useState(null);
  const [solvePdfName, setSolvePdfName] = useState('');
  const [solution, setSolution] = useState(null);
  const [solveLoading, setSolveLoading] = useState(false);

  // Check access on mount - only redirect if truly no access
  useEffect(() => {
    // If user is logged in, they have access (free or premium)
    if (token) return;
    
    // If trial email exists and trial is active, they have access
    if (trialEmail && isTrialActive) return;
    
    // Check if we're loading trial status
    if (trialEmail && trialStatus === null) {
      checkTrialStatus(trialEmail);
      return;
    }
    
    // No valid access - redirect to landing
    if (!token && !isTrialActive) {
      navigate('/');
    }
  }, [token, trialEmail, isTrialActive, trialStatus]);

  // Handle preset from navigation state
  useEffect(() => {
    if (location.state?.preset) {
      handlePresetClick(location.state.preset);
    }
  }, [location.state]);

  const handlePresetClick = (preset) => {
    setSelectedPreset(preset);
    setNumQuestions(PRESET_QUESTION_COUNT);
  };

  // Question Solver Handler
  const handleSolveQuestion = async () => {
    setError('');
    setSolution(null);
    
    // Check trial limits
    if (!user && !isSubscribed) {
      if (!isTrialActive) {
        setError('Your trial has expired. Please subscribe to continue.');
        setShowUpgradeModal(true);
        return;
      }
      
      if (!canGenerateQuiz()) {
        setError(`Daily limit reached (${quizzesLimit} quizzes). Try again tomorrow or subscribe for unlimited access.`);
        setShowUpgradeModal(true);
        return;
      }
    }
    
    if (solveInputMode === 'text' && !solveQuestion.trim()) {
      setError('Please enter a question to solve');
      return;
    }
    
    if (solveInputMode === 'image' && !solveFile) {
      setError('Please upload an image');
      return;
    }
    
    if (solveInputMode === 'pdf' && !solvePdfFile) {
      setError('Please upload a PDF');
      return;
    }
    
    setSolveLoading(true);
    
    try {
      // Note: Usage tracking handled by backend via X-Trial-Email header
      
      let response;
      
      // Build headers - include trial email for trial users
      const buildHeaders = () => {
        const headers = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        } else if (trialEmail) {
          headers['X-Trial-Email'] = trialEmail;
        }
        return headers;
      };
      
      if (solveInputMode === 'text') {
        response = await axios.post(`${API_URL}/api/quiz/solve`, {
          question: solveQuestion
        }, {
          headers: buildHeaders()
        });
      } else if (solveInputMode === 'image') {
        const formData = new FormData();
        formData.append('file', solveFile);
        formData.append('additional_context', solveQuestion || '');
        
        response = await axios.post(`${API_URL}/api/quiz/solve-from-image`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...buildHeaders()
          }
        });
      } else if (solveInputMode === 'pdf') {
        const formData = new FormData();
        formData.append('file', solvePdfFile);
        formData.append('additional_context', solveQuestion || '');
        
        response = await axios.post(`${API_URL}/api/quiz/solve-from-pdf`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...buildHeaders()
          }
        });
      }
      
      setSolution(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to solve question');
    } finally {
      setSolveLoading(false);
    }
  };

  const handleSolveFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setSolveFile(selectedFile);
      setSolveFileName(selectedFile.name);
    }
  };

  const handleSolvePdfChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setSolvePdfFile(selectedFile);
      setSolvePdfName(selectedFile.name);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.toLowerCase().endsWith('.pdf')) {
        setError('Only PDF files are supported');
        return;
      }
      if (selectedFile.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      setFile(selectedFile);
      setFileName(selectedFile.name);
      setError('');
      // Track PDF file upload
      trackStudyMaterialUploaded('pdf', Math.round(selectedFile.size / 1024));
    }
  };

  const handleImageChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const validFormats = ['.jpg', '.jpeg', '.png'];
      const fileExt = selectedFile.name.toLowerCase().slice(selectedFile.name.lastIndexOf('.'));
      
      if (!validFormats.includes(fileExt)) {
        setError('Only JPG and PNG images are supported');
        return;
      }
      if (selectedFile.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }
      setImageFile(selectedFile);
      setImageName(selectedFile.name);
      setError('');
      // Track image file upload
      trackStudyMaterialUploaded('image', Math.round(selectedFile.size / 1024));
    }
  };

  const handleAudioChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > 25 * 1024 * 1024) {
        setError('File size must be less than 25MB');
        return;
      }
      setAudioFile(selectedFile);
      setAudioName(selectedFile.name);
      setError('');
      // Track audio file upload
      trackStudyMaterialUploaded('audio', Math.round(selectedFile.size / 1024));
    }
  };

  // Track input mode selection for study material type
  const handleInputModeChange = (mode) => {
    setInputMode(mode);
    trackStudyMaterialSelected(mode);
  };

  const handleGenerate = async () => {
    setError('');
    
    // Check if user can generate quiz (trial limits)
    if (!user && !isSubscribed) {
      if (!isTrialActive) {
        setError('Your trial has expired. Please subscribe to continue.');
        setShowUpgradeModal(true);
        return;
      }
      
      if (!canGenerateQuiz()) {
        setError(`Daily limit reached (${quizzesLimit} quizzes). Try again tomorrow or subscribe for unlimited access.`);
        setShowUpgradeModal(true);
        return;
      }
    }
    
    if (inputMode === 'text' && !text.trim()) {
      setError('Please enter some text');
      return;
    }
    
    if (inputMode === 'url' && !url.trim()) {
      setError('Please enter a URL');
      return;
    }

    if (inputMode === 'file' && !file) {
      setError('Please select a PDF file');
      return;
    }

    if (inputMode === 'image' && !imageFile) {
      setError('Please select an image file');
      return;
    }

    if (inputMode === 'audio' && !audioFile) {
      setError('Please select an audio/video file');
      return;
    }

    setLoading(true);

    // Track quiz generation started
    const userType = isPremium ? 'premium' : (isTrialActive ? 'trial' : 'free');
    trackQuizGenerationStarted(inputMode, userType);

    try {
      // Note: Quiz recording is handled by backend via X-Trial-Email header
      // No need to call recordQuizGeneration() here - backend records after successful generation

      let response;
      let endpoint;
      
      if (inputMode === 'file') {
        // Handle PDF upload
        const formData = new FormData();
        formData.append('file', file);
        formData.append('difficulty', difficulty);
        formData.append('num_questions', numQuestions);
        formData.append('title', `Quiz from ${fileName}`);
        if (selectedPreset) formData.append('preset', selectedPreset);
        endpoint = `${API_URL}/api/quiz/generate-from-file`;

        // Build headers
        const headers = { 'Content-Type': 'multipart/form-data' };
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        } else if (trialEmail) {
          headers['X-Trial-Email'] = trialEmail;
        }

        response = await axios.post(endpoint, formData, { headers });
      } else if (inputMode === 'image') {
        // Handle image upload with OCR
        const formData = new FormData();
        formData.append('file', imageFile);
        formData.append('difficulty', difficulty);
        formData.append('num_questions', numQuestions);
        formData.append('title', `Quiz from ${imageName}`);
        if (selectedPreset) formData.append('preset', selectedPreset);
        endpoint = `${API_URL}/api/quiz/generate-from-image`;

        // Build headers
        const headers = { 'Content-Type': 'multipart/form-data' };
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        } else if (trialEmail) {
          headers['X-Trial-Email'] = trialEmail;
        }

        response = await axios.post(endpoint, formData, { headers });
      } else if (inputMode === 'audio') {
        // Handle audio/video upload with transcription
        const formData = new FormData();
        formData.append('file', audioFile);
        formData.append('difficulty', difficulty);
        formData.append('num_questions', numQuestions);
        formData.append('title', `Quiz from ${audioName}`);
        if (selectedPreset) formData.append('preset', selectedPreset);
        endpoint = `${API_URL}/api/quiz/generate-from-audio`;

        // Build headers
        const headers = { 'Content-Type': 'multipart/form-data' };
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        } else if (trialEmail) {
          headers['X-Trial-Email'] = trialEmail;
        }

        response = await axios.post(endpoint, formData, { headers });
      } else {
        // Handle text or URL
        const payload = {
          [inputMode]: inputMode === 'text' ? text : url,
          difficulty,
          num_questions: numQuestions
        };
        if (selectedPreset) payload.preset = selectedPreset;

        // Build headers - include trial email for trial users
        const headers = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        } else if (trialEmail) {
          headers['X-Trial-Email'] = trialEmail;
        }

        response = await axios.post(
          `${API_URL}/api/quiz/generate`,
          payload,
          { headers }
        );
      }

      const quizId = response.data.quiz_id;
      const questionCount = response.data.question_count || numQuestions;
      
      // Track successful quiz generation
      trackQuizGeneratedSuccess(questionCount, inputMode, userType);
      
      navigate(`/quiz/take/${quizId}`);
    } catch (err) {
      // Track failed quiz generation
      const errorType = err.response?.status === 429 ? 'rate_limit' : 
                        err.response?.status === 400 ? 'invalid_input' : 'server_error';
      trackQuizGenerationFailed(inputMode, errorType, userType);
      
      setError(err.response?.data?.detail || 'Failed to generate quiz. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle clicking on a locked feature
  const handleLockedFeatureClick = (feature) => {
    setLockedFeature(feature);
    setShowUpgradeModal(true);
  };

  // Check if user can use a specific input mode
  const canUseInputMode = (mode) => {
    // Trial users have full access
    if (isTrialActive && !user) return true;
    // Premium users have full access
    if (isPremium) return true;
    // Free tier users can only use text
    if (mode === 'text') return true;
    return false;
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Upgrade Modal */}
      <UpgradeModal 
        isOpen={showUpgradeModal} 
        onClose={() => {
          setShowUpgradeModal(false);
          setLockedFeature(null);
        }}
        feature={lockedFeature}
      />

      {/* Navigation */}
      <nav className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm border-b`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <button
              onClick={() => navigate('/')}
              className={`flex items-center ${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>
            
            {/* Trial Status Badge */}
            {!user && isTrialActive && !isSubscribed && (
              <div className={`flex items-center space-x-4 text-sm`}>
                <span className={`px-3 py-1 rounded-full ${isDarkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'}`}>
                  Trial: {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} left
                </span>
                <span className={`px-3 py-1 rounded-full ${
                  quizzesToday >= quizzesLimit 
                    ? (isDarkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-700')
                    : (isDarkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-700')
                }`}>
                  {quizzesLimit - quizzesToday} quiz{quizzesLimit - quizzesToday !== 1 ? 'zes' : ''} left today
                </span>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <h1 className={`text-4xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {mainMode === 'generate' ? 'Generate Quiz ✨' : 'Solve Question 🧠'}
          </h1>
          <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {mainMode === 'generate' 
              ? 'Transform any study material into an interactive quiz' 
              : 'Get detailed solutions with step-by-step explanations'}
          </p>
        </div>

        {/* Page Title */}
        <div className="text-center mb-8">
          <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            ✨ Generate Quiz
          </h2>
          <p className={`mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Create quizzes from text, URL, PDF, images, or audio
          </p>
        </div>

        {/* GENERATE QUIZ MODE */}
        <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-sm p-8 border`}>
          {error && (
            <div className="mb-6 p-4 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-400 rounded-lg">
              {error}
            </div>
          )}

          {/* Exam-Style Presets */}
          <div className="mb-8 pb-8 border-b border-gray-200 dark:border-gray-700">
            <label className={`block text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Exam-Style Presets</label>
            <p className={`text-xs mb-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>Quick setup for exam preparation (sets {PRESET_QUESTION_COUNT} questions)</p>
            <div className="overflow-x-auto -mx-2 px-2 pb-2">
              <div className="flex gap-3 min-w-max">
                {EXAM_PRESETS.map((preset) => (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => handlePresetClick(preset.id)}
                    className={`p-4 border-2 rounded-lg transition-all min-w-[140px] ${
                      selectedPreset === preset.id
                        ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/30'
                        : isDarkMode 
                          ? 'border-gray-600 hover:border-indigo-400 hover:bg-gray-700' 
                          : 'border-gray-300 hover:border-indigo-400 hover:bg-gray-50'
                    }`}
                  >
                    <div className="text-2xl mb-2">{preset.icon}</div>
                    <div className={`font-bold text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{preset.name}</div>
                    <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{preset.desc}</div>
                  </button>
                ))}
              </div>
            </div>
            {selectedPreset && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <span className="font-medium">✓ {selectedPreset.toUpperCase().replace(/-/g, ' ')} preset active:</span> {PRESET_QUESTION_COUNT} questions configured with exam-style question mix. You can still adjust difficulty below.
                </p>
              </div>
            )}
          </div>

          {/* Input Mode Selector */}
          <div className="mb-8">
            <label className={`block text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Input Source</label>
            <div className="grid grid-cols-5 gap-3">
              {/* Text - Always available */}
              <button
                onClick={() => handleInputModeChange('text')}
                className={`p-3 border-2 rounded-lg text-center transition-all ${
                  inputMode === 'text'
                    ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                    : isDarkMode 
                      ? 'border-gray-600 hover:border-gray-500 text-gray-300' 
                      : 'border-gray-300 hover:border-gray-400 text-gray-700'
                }`}
              >
                <div className="text-2xl mb-1">📝</div>
                <div className="font-medium text-sm">Text</div>
              </button>
              
              {/* URL - Premium only */}
              <button
                onClick={() => canUseInputMode('url') ? handleInputModeChange('url') : handleLockedFeatureClick('url')}
                className={`p-3 border-2 rounded-lg text-center transition-all relative ${
                  !canUseInputMode('url')
                    ? 'border-gray-300 dark:border-gray-600 opacity-60 cursor-pointer'
                    : inputMode === 'url'
                      ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                      : isDarkMode 
                        ? 'border-gray-600 hover:border-gray-500 text-gray-300' 
                        : 'border-gray-300 hover:border-gray-400 text-gray-700'
                }`}
              >
                {!canUseInputMode('url') && (
                  <div className="absolute -top-1 -right-1 text-sm">🔒</div>
                )}
                <div className="text-2xl mb-1">🔗</div>
                <div className="font-medium text-sm">URL</div>
              </button>
              
              {/* PDF - Premium only */}
              <button
                onClick={() => canUseInputMode('file') ? handleInputModeChange('file') : handleLockedFeatureClick('pdf')}
                className={`p-3 border-2 rounded-lg text-center transition-all relative ${
                  !canUseInputMode('file')
                    ? 'border-gray-300 dark:border-gray-600 opacity-60 cursor-pointer'
                    : inputMode === 'file'
                      ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                      : isDarkMode 
                        ? 'border-gray-600 hover:border-gray-500 text-gray-300' 
                        : 'border-gray-300 hover:border-gray-400 text-gray-700'
                }`}
              >
                {!canUseInputMode('file') && (
                  <div className="absolute -top-1 -right-1 text-sm">🔒</div>
                )}
                <div className="text-2xl mb-1">📄</div>
                <div className="font-medium text-sm">PDF</div>
              </button>
              
              {/* Image - Premium only */}
              <button
                onClick={() => canUseInputMode('image') ? handleInputModeChange('image') : handleLockedFeatureClick('image')}
                className={`p-3 border-2 rounded-lg text-center transition-all relative ${
                  !canUseInputMode('image')
                    ? 'border-gray-300 dark:border-gray-600 opacity-60 cursor-pointer'
                    : inputMode === 'image'
                      ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                      : isDarkMode 
                        ? 'border-gray-600 hover:border-gray-500 text-gray-300' 
                        : 'border-gray-300 hover:border-gray-400 text-gray-700'
                }`}
              >
                {!canUseInputMode('image') && (
                  <div className="absolute -top-1 -right-1 text-sm">🔒</div>
                )}
                <div className="text-2xl mb-1">🖼️</div>
                <div className="font-medium text-sm">Image</div>
              </button>
              
              {/* Audio - Premium only */}
              <button
                onClick={() => canUseInputMode('audio') ? handleInputModeChange('audio') : handleLockedFeatureClick('audio')}
                className={`p-3 border-2 rounded-lg text-center transition-all relative ${
                  !canUseInputMode('audio')
                    ? 'border-gray-300 dark:border-gray-600 opacity-60 cursor-pointer'
                    : inputMode === 'audio'
                      ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                      : isDarkMode 
                        ? 'border-gray-600 hover:border-gray-500 text-gray-300' 
                        : 'border-gray-300 hover:border-gray-400 text-gray-700'
                }`}
              >
                {!canUseInputMode('audio') && (
                  <div className="absolute -top-1 -right-1 text-sm">🔒</div>
                )}
                <div className="text-2xl mb-1">🎵</div>
                <div className="font-medium text-sm">Audio</div>
              </button>
            </div>
          </div>

          {/* Content Input */}
          {inputMode === 'text' ? (
            <div className="mb-6">
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Enter Study Material
              </label>
              <textarea
                value={text}
                onChange={(e) => {
                  const charLimit = getTextCharLimit();
                  if (charLimit && e.target.value.length > charLimit) {
                    setText(e.target.value.slice(0, charLimit));
                    setError(`Text limit reached (${charLimit.toLocaleString()} characters for free tier). Upgrade for unlimited text.`);
                  } else {
                    setText(e.target.value);
                    if (error && error.includes('Text limit')) setError('');
                  }
                }}
                rows={12}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                  isDarkMode 
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                }`}
                placeholder="Paste your study material here...

Example: The mitochondria is the powerhouse of the cell. It generates ATP through cellular respiration..."
              />
              <div className={`mt-2 flex justify-between text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                <span>Minimum 50 characters</span>
                {getTextCharLimit() && (
                  <span className={text.length >= getTextCharLimit() * 0.9 ? 'text-orange-500 font-medium' : ''}>
                    {text.length.toLocaleString()} / {getTextCharLimit().toLocaleString()} characters
                    {!isPremium && <span className="ml-1 text-xs">(Free tier limit)</span>}
                  </span>
                )}
              </div>
            </div>
          ) : inputMode === 'url' ? (
            <div className="mb-6">
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Enter URL or PDF Link
              </label>
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                  isDarkMode 
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                }`}
                placeholder="https://example.com/article or https://example.com/document.pdf"
              />
              <p className={`mt-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                📄 Supports web pages, PDFs, and research papers with LaTeX equations
              </p>
            </div>
          ) : inputMode === 'file' ? (
            <div className="mb-6">
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Upload PDF File
              </label>
              <div className={`border-2 border-dashed rounded-lg p-8 text-center hover:border-indigo-400 transition-colors ${
                isDarkMode ? 'border-gray-600' : 'border-gray-300'
              }`}>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                  className="hidden"
                  id="pdf-upload"
                />
                <label
                  htmlFor="pdf-upload"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <div className="text-5xl mb-4">📄</div>
                  {fileName ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-center space-x-2 text-green-600">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="font-medium">{fileName}</span>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setFile(null);
                          setFileName('');
                        }}
                        className="text-sm text-red-600 hover:text-red-800"
                      >
                        Remove file
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Click to upload PDF</p>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>or drag and drop</p>
                    </div>
                  )}
                </label>
              </div>
              <p className={`mt-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                📄 Maximum file size: 10MB • Supports up to 30 pages
              </p>
            </div>
          ) : inputMode === 'image' ? (
            <div className="mb-6">
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Upload Image (JPG/PNG)
              </label>
              <div className={`border-2 border-dashed rounded-lg p-8 text-center hover:border-indigo-400 transition-colors ${
                isDarkMode ? 'border-gray-600' : 'border-gray-300'
              }`}>
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png"
                  onChange={handleImageChange}
                  className="hidden"
                  id="image-upload"
                />
                <label
                  htmlFor="image-upload"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <div className="text-5xl mb-4">🖼️</div>
                  {imageName ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-center space-x-2 text-green-600">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="font-medium">{imageName}</span>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setImageFile(null);
                          setImageName('');
                        }}
                        className="text-sm text-red-600 hover:text-red-800"
                      >
                        Remove file
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Click to upload image</p>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>JPG or PNG with text content</p>
                    </div>
                  )}
                </label>
              </div>
              <p className={`mt-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                🖼️ Maximum size: 5MB • AI will extract text using OCR
              </p>
            </div>
          ) : inputMode === 'audio' ? (
            <div className="mb-6">
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Upload Audio/Video File
              </label>
              <div className={`border-2 border-dashed rounded-lg p-8 text-center hover:border-indigo-400 transition-colors ${
                isDarkMode ? 'border-gray-600' : 'border-gray-300'
              }`}>
                <input
                  type="file"
                  accept=".mp3,.wav,.m4a,.aac,.ogg,.flac,.mp4,.avi,.mov,.mkv,.wmv"
                  onChange={handleAudioChange}
                  className="hidden"
                  id="audio-upload"
                />
                <label
                  htmlFor="audio-upload"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <div className="text-5xl mb-4">🎵</div>
                  {audioName ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-center space-x-2 text-green-600">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="font-medium">{audioName}</span>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setAudioFile(null);
                          setAudioName('');
                        }}
                        className="text-sm text-red-600 hover:text-red-800"
                      >
                        Remove file
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Click to upload audio/video</p>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>MP3, WAV, MP4, and more</p>
                    </div>
                  )}
                </label>
              </div>
              <p className={`mt-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                🎵 Maximum size: 25MB • AI will transcribe speech to text
              </p>
            </div>
          ) : null}

          {/* Difficulty Selector */}
          <div className="mb-6">
            <label className={`block text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Difficulty Level</label>
            <div className="grid grid-cols-3 gap-4">
              {['easy', 'medium', 'hard'].map((level) => (
                <button
                  key={level}
                  onClick={() => setDifficulty(level)}
                  className={`p-3 border-2 rounded-lg text-center transition-all capitalize ${
                    difficulty === level
                      ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                      : isDarkMode 
                        ? 'border-gray-600 hover:border-gray-500 text-gray-300' 
                        : 'border-gray-300 hover:border-gray-400 text-gray-700'
                  }`}
                >
                  {level}
                </button>
              ))}
            </div>
          </div>

          {/* Number of Questions */}
          <div className="mb-8">
            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Number of Questions: {numQuestions}
              {!isPremium && !isTrialActive && numQuestions > FREE_TIER_MAX_QUESTIONS && (
                <span className="ml-2 text-orange-500 text-xs">(Max {FREE_TIER_MAX_QUESTIONS} for free tier)</span>
              )}
            </label>
            <input
              type="range"
              min="4"
              max={isPremium || isTrialActive ? 20 : FREE_TIER_MAX_QUESTIONS}
              value={Math.min(numQuestions, isPremium || isTrialActive ? 20 : FREE_TIER_MAX_QUESTIONS)}
              onChange={(e) => setNumQuestions(parseInt(e.target.value))}
              className="w-full"
            />
            <div className={`flex justify-between text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <span>4</span>
              <span>{isPremium || isTrialActive ? '12' : '5'}</span>
              <span>{isPremium || isTrialActive ? '20' : FREE_TIER_MAX_QUESTIONS}</span>
            </div>
            {!isPremium && !isTrialActive && (
              <p className="text-xs text-orange-500 mt-1">
                🔒 Free tier limited to {FREE_TIER_MAX_QUESTIONS} questions. <button onClick={() => handleLockedFeatureClick('default')} className="underline">Upgrade for up to 20</button>
              </p>
            )}
          </div>

          {/* Free Tier Daily Limit Notice */}
          {user && isFree && (
            <div className={`mb-6 p-4 rounded-lg ${isDarkMode ? 'bg-amber-900/30 border border-amber-700' : 'bg-amber-50 border border-amber-200'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <span className="text-2xl mr-3">📊</span>
                  <div>
                    <p className={`font-medium ${isDarkMode ? 'text-amber-200' : 'text-amber-800'}`}>
                      Free Plan: {getDailyQuizzesRemaining()} of 3 quizzes remaining today
                    </p>
                    <p className={`text-sm ${isDarkMode ? 'text-amber-300/80' : 'text-amber-700'}`}>
                      Upgrade for unlimited quizzes and all features
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/pricing')}
                  className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg text-sm font-medium hover:from-indigo-700 hover:to-purple-700"
                >
                  Upgrade
                </button>
              </div>
            </div>
          )}

          {/* Generate Button */}
          <button
            onClick={handleGenerate}
            disabled={loading}
            className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-4 px-6 rounded-lg font-medium hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-lg"
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <svg className="animate-spin h-5 w-5 mr-3 text-white" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Generating Quiz...
              </div>
            ) : (
              '✨ Generate Quiz'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuizGenerate;
